import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, BadgeDollarSign, Clock, Award, TrendingUp } from 'lucide-react';
import InstallerLayout from './InstallerLayout';
import { getBidsByInstaller, getCurrentInstallerId, getJob } from '../../lib/data';

export default function MyBids({ user }) {
  const installerId = getCurrentInstallerId(user);
  const [bids, setBids] = useState([]);

  useEffect(() => {
    setBids(getBidsByInstaller(installerId));
  }, [installerId]);

  const stats = useMemo(() => {
    if (!bids.length) {
      return {
        total: 0,
        awarded: 0,
        pending: 0,
        avgAmount: 0,
      };
    }
    const awarded = bids.filter((b) => b.status === 'awarded').length;
    const pending = bids.filter((b) => b.status === 'submitted' || b.status === 'shortlisted').length;
    const avgAmount =
      bids.reduce((sum, b) => sum + Number(b.amountTotal || 0), 0) / Math.max(1, bids.length);
    return {
      total: bids.length,
      awarded,
      pending,
      avgAmount,
    };
  }, [bids]);

  return (
    <InstallerLayout>
      <div className="space-y-6" data-testid="installer-my-bids-page">
        {/* Header & stats */}
        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-blue-600 flex items-center gap-2">
                <BadgeDollarSign className="w-4 h-4" /> Bid pipeline
              </p>
              <h1 className="mt-1 text-xl md:text-2xl font-bold text-gray-900">My bids</h1>
              <p className="mt-1 text-sm text-gray-600 max-w-xl">
                Track all bids in one place, from first quote to awarded jobs. Click a bid to edit or
                refine your pricing.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 min-w-[260px]">
              <div className="bg-gray-50 rounded-xl p-3 text-center">
                <div className="text-[11px] text-gray-500">Total bids</div>
                <div className="text-lg font-semibold" data-testid="bids-count">
                  {stats.total}
                </div>
              </div>
              <div className="bg-emerald-50 rounded-xl p-3 text-center">
                <div className="text-[11px] text-emerald-700">Awarded</div>
                <div className="text-lg font-semibold text-emerald-800" data-testid="bids-awarded-count">
                  {stats.awarded}
                </div>
              </div>
              <div className="bg-amber-50 rounded-xl p-3 text-center">
                <div className="text-[11px] text-amber-700">In play</div>
                <div className="text-lg font-semibold text-amber-800" data-testid="bids-pending-count">
                  {stats.pending}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-gray-500">
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 text-blue-700">
              <TrendingUp className="w-3 h-3" />
              Avg bid: ${stats.avgAmount.toFixed(0).toLocaleString()}
            </div>
            <div className="inline-flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
              Awarded
            </div>
            <div className="inline-flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
              Submitted / shortlisted
            </div>
            <div className="inline-flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-gray-400" />
              Lost / withdrawn
            </div>
          </div>
        </section>

        {/* Bids list */}
        <div className="space-y-3">
          {bids.map((b) => {
            const job = getJob(b.jobId);
            const status = b.status || 'submitted';
            const statusStyle =
              status === 'awarded'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                : status === 'shortlisted' || status === 'submitted'
                  ? 'bg-amber-50 text-amber-700 border-amber-100'
                  : 'bg-gray-50 text-gray-600 border-gray-200';

            return (
              <div
                key={b.id}
                data-testid={`my-bid-${b.id}`}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all p-4 flex flex-col gap-3"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-base font-semibold text-gray-900">
                        ${b.amountTotal.toLocaleString()}
                      </div>
                      <span className="text-xs text-gray-500">
                        {b.timelineDays} days • {b.warrantyYears} yr warranty
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${statusStyle}`}
                      >
                        {status}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-gray-600">
                      {job?.title} — {job?.address}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      ${b.pricePerSqFt.toFixed(2)}/sq ft • valid until{' '}
                      {b.validityUntil || 'date not set'}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>Updated {new Date(b.updatedAt || b.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="mt-1 flex gap-2">
                      <Link
                        to={`/installer/jobs/${b.jobId}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100"
                        data-testid={`my-bid-view-${b.id}`}
                      >
                        Edit bid
                        <ArrowUpRight className="w-3 h-3" />
                      </Link>
                      <Link
                        to={`/compare-bids?jobId=${b.jobId}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100"
                        data-testid={`my-bid-compare-${b.id}`}
                      >
                        Compare
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {bids.length === 0 && (
            <div className="bg-white rounded-2xl border border-dashed border-gray-300 py-10 text-center text-gray-500 text-sm">
              You haven&apos;t submitted any bids yet. Browse open jobs and create your first bid to
              kickstart your pipeline.
            </div>
          )}
        </div>
      </div>
    </InstallerLayout>
  );
}
